import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

const List = chakra('ol', {
  base: { listStyleType: 'none', margin: 0, padding: 0, minWidth: 0 },
});

const Item = chakra('li', {
  base: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'var(--tl-gutter) 1fr',
    columnGap: '3',
    minWidth: 0,
    // the connecting rail, drawn behind the markers down the gutter
    _before: {
      content: '""',
      position: 'absolute',
      left: 'calc(var(--tl-gutter) / 2)',
      top: '0',
      bottom: '0',
      width: '1.5px',
      transform: 'translateX(-50%)',
      bg: 'border.default',
    },
    // the top item's rail starts at its marker, the last item's ends at its marker
    _first: { _before: { top: 'var(--tl-marker-offset)' } },
    _last: { _before: { bottom: 'calc(100% - var(--tl-marker-offset))' } },
    // single item → no rail
    '&:only-child': { _before: { display: 'none' } },
  },
});

const Marker = chakra('span', {
  base: {
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: 'var(--tl-gutter)',
    mt: 'calc(var(--tl-marker-offset) - (var(--tl-gutter) / 2))',
    color: 'text.muted',
  },
});

const Body = chakra('div', { base: { minWidth: 0, pb: '3' } });

export interface TimelineItemProps extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'color'> {
  /** The marker rendered on the rail (a dot, icon, or index chip). */
  marker?: ReactNode;
  children: ReactNode;
}

/** One entry on a {@link Timeline}: a rail marker plus arbitrary body content. */
export const TimelineItem = forwardRef<HTMLLIElement, TimelineItemProps>(function TimelineItem(
  { marker, children, ...rest },
  ref,
) {
  return (
    <Item ref={ref} {...rest}>
      <Marker aria-hidden>{marker ?? <Dot />}</Marker>
      <Body>{children}</Body>
    </Item>
  );
});

const Dot = chakra('span', {
  base: { boxSize: '2.5', borderRadius: 'full', bg: 'currentColor' },
});

export interface TimelineProps extends Omit<React.OlHTMLAttributes<HTMLOListElement>, 'color'> {
  children: ReactNode;
  /** Vertical offset of the marker from the top of each item's body (aligns to a header). */
  markerOffset?: string;
  gutter?: string;
}

/**
 * A vertical timeline — a connecting rail with per-item markers — for step-by-step
 * sequences like the run transcript. Purely presentational; the caller owns each item's
 * content (e.g. a collapsible transcript step).
 */
export const Timeline = forwardRef<HTMLOListElement, TimelineProps>(function Timeline(
  { children, markerOffset = '1.25rem', gutter = '1.5rem', ...rest },
  ref,
) {
  const vars = {
    '--tl-gutter': gutter,
    '--tl-marker-offset': markerOffset,
  } as HTMLChakraProps<'ol'>['style'];
  return (
    <List ref={ref} style={vars} {...rest}>
      {children}
    </List>
  );
});
